import { HttpErrorResponse } from "@angular/common/http";
import { Component, OnDestroy, OnInit, signal, ViewChild, inject } from "@angular/core";
import { ChartJsContainerComponent, rgb } from "@tylertech/chart-js";
import { ChartBindingObject, ChartDialogService, ChartManagerService, ChartRegistryService, ChartService } from "@tylertech/charting-module";
import { Command, IconCommand } from "@tylertech/commands-module";
import { formatAbbreviatedNumber } from "@tylertech/common-components-module";
import { FileService, LoggingService } from "@tylertech/core-module";
import { ICustomViewComponent } from "@tylertech/custom-view";
import { Utilities } from "@tylertech/shared-module";
import { IChartCriteria } from "@tylertech/tyler-api-types";
import { ChartConfiguration, ChartEvent, LegendElement, LegendItem, TooltipItem } from "chart.js";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

@Component({
    selector: "payroll-budgeted-positions-chart",
    templateUrl: "./budgetedPositionsChart.tmpl.html",
    standalone: false
})
export class BudgetedPositionsChartComponent implements OnDestroy, OnInit, ICustomViewComponent<ChartBindingObject>
{
    private readonly $chartRegistryService = inject(ChartRegistryService);
    private readonly $loggingService = inject(LoggingService);
    private readonly $chartDialogService = inject(ChartDialogService);
    private readonly $fileService = inject(FileService);
    private readonly $chartService = inject(ChartService);

    private readonly destructor$ = new Subject<void>();
    readonly customDataBinding: ChartBindingObject;
    readonly commands: ReadonlyArray<Command>;
    readonly ChartTitle: string = "Budgeted Positions";
    private _chartCriteria: IChartCriteria;
    chartConfig: ChartConfiguration<"bar">;
    @ViewChild(ChartJsContainerComponent) private readonly chartContainer: ChartJsContainerComponent;
    readonly showChart = signal(false);

    isLoaded: boolean;
    ChartSubtitle: string;
    constructor()
    {
        const $chartManagerService = inject(ChartManagerService);

        $chartManagerService.refreshChartObservable.pipe(takeUntil(this.destructor$)).subscribe((chartId: string) =>
        {
            if (chartId !== this.chartId)
            {
                return;
            }
            this.loadChartData();
        });
        this.commands = [
            new IconCommand({
                icon: "share",
                label: null,
                action: () => this.shareChart(),
                getEnabled: () => this.hasData(),
                tooltip: `Share ${this.ChartTitle}`,
            }),
        ];
    }

    get chartId(): string | undefined
    {
        return this.customDataBinding?.chartId;
    }

    get chartCriteria(): IChartCriteria | undefined
    {
        return this._chartCriteria || this.customDataBinding?.chartCriteria;
    }

    set chartCriteria(value: IChartCriteria)
    {
        this._chartCriteria = value;
    }

    get errorMessage(): string | undefined
    {
        return this.chartCriteria?.ErrorMessage;
    }

    ngOnDestroy(): void
    {
        this.destructor$.next();
        this.destructor$.complete();
    }

    ngOnInit(): void
    {
        new Promise((_resolveFn, rejectFn) =>
        {
            if (!this.hasData())
            {
                return this.loadChartData();
            }
            try
            {
                this.updateChartConfiguration();
            }
            catch (err)
            {
                rejectFn(err);
            }
        })
            .catch(e =>
            {
                console.error("Error on load", e);
            })
            .finally(() =>
            {
                this.isLoaded = true;
            });
    }

    private getDepartmentText(): string
    {
        try
        {
            const departments = this.chartCriteria?.Parameters?.find(v => v.Id === "BudgetedPositionsDepartmentIds");
            const dept = departments.Value?.split(",");
            const deptCodes = dept.map(g => departments?.Options.find(v => v.Value === g).Display);
            const display = deptCodes.join(", ");
            return display;
        }
        catch
        {
            /* Squashing error */
        }
        return null;
    }

    private getFiscalName(): string
    {
        try
        {
            const fiscals = this.chartCriteria?.Parameters?.filter(v => v.Id === "BudgetedPositionsFiscal1")[0];
            const fisc = (<string>fiscals.Value).split(",");
            const fiscCodes = fisc.map(g => fiscals.Options.filter(v => v.Value === g)[0].Display);
            const display = fiscCodes.join(", ");
            return display;
        }
        catch
        {
            /* Squashing error */
        }
        return "";
    }

    private getPositionsText(): string
    {
        try
        {
            const positions = this.chartCriteria?.Parameters?.filter(v => v.Id === "BudgetedPositionsPosIds")[0];
            const pos = (<string>positions.Value).split(",");
            const posCodes = pos.map(g => positions.Options.filter(v => v.Value === g)[0].Display);
            const display = posCodes.join(", ");
            return display;
        }
        catch
        {
            /* Squashing error */
        }
        return null;
    }

    hasData(): boolean
    {
        return this.chartCriteria?.ChartDatas?.length > 0;
    }

    private loadChartData(): Promise<void>
    {
        this.isLoaded = false;
        return this.$chartRegistryService
            .GetChartData(this.chartCriteria)
            .then(
                (result: IChartCriteria) =>
                {
                    this.chartCriteria = result;
                },
                (error: HttpErrorResponse) =>
                {
                    this.$loggingService.error("Could not retrieve chart data", error);
                    this.chartCriteria = {
                        ErrorMessage: error.error.Message,
                    } as IChartCriteria;
                }
            )
            .then(
                () =>
                {
                    if (!this.hasData())
                    {
                        return;
                    }
                    try
                    {
                        this.updateChartConfiguration();
                    }
                    catch (err)
                    {
                        return Promise.reject(err);
                    }
                },
                (err: unknown) =>
                {
                    console.error(err);
                }
            )
            .finally(() =>
            {
                setTimeout(() =>
                {
                    this.isLoaded = true;
                }, 50);
            });
    }

    openSettings(): void
    {
        this.$chartDialogService
            .showChartParametersDialog(this.ChartTitle, this.chartCriteria)
            .then((criteria: IChartCriteria) =>
            {
                this.chartCriteria = criteria;
                this.loadChartData();
            });
    }

    private shareChart(): void
    {
        const img = this.chartContainer.createImage();
        this.$fileService.openDocumentLink(img, "image/png", this.ChartTitle.replace(/ /g, ""));
    }

    private updateChartConfiguration(): void
    {
        let subTitle = this.getFiscalName();
        const department = this.getDepartmentText();
        const position = this.getPositionsText();

        if (department)
        {
            subTitle = subTitle + " &bull; " + department;
        }
        if (position)
        {
            subTitle = subTitle + " &bull; " + position;
        }

        this.ChartSubtitle = subTitle;

        const budgeted = this.chartCriteria.ChartDatas[0];
        const filled = this.chartCriteria.ChartDatas[1];
        const vacant = this.chartCriteria.ChartDatas[2];
        const closed = this.chartCriteria.ChartDatas[3];

        const chartColors = this.$chartService.getColors(4);

        this.chartConfig = {
            type: "bar",
            options: {
                responsive: true,
                plugins: {
                    legend: {
                        display: true,
                        position: "right",
                        labels: { boxWidth: 12 },
                        onClick: (_e: ChartEvent, legendItem: LegendItem, legend: LegendElement<"bar">): void =>
                        {
                            const index = legendItem.datasetIndex;
                            const ci = legend.chart;
                            const meta = ci.getDatasetMeta(index);
                            const yAxisID = meta.yAxisID;
                            meta.hidden = Utilities.IsNullOrUndefined(meta.hidden) ? !ci.data.datasets[index].hidden : null;
                            let showScale = false;
                            for (let i = 0; i < (ci.data.datasets || []).length; ++i)
                            {
                                const iMeta = ci.getDatasetMeta(i);
                                if (Utilities.IsNullOrUndefined(iMeta.hidden) && iMeta.yAxisID === yAxisID)
                                {
                                    showScale = true;
                                    break;
                                }
                            }
                            ci.options.scales[yAxisID].display = showScale;
                            ci.update();
                        },
                    },
                    tooltip: {
                        callbacks: {
                            title: (tooltipItems: TooltipItem<"bar">[]) =>
                            {
                                return this.chartCriteria.ChartDatas[0].Labels[tooltipItems[0].dataIndex];
                            },
                            label: (tooltipItem: TooltipItem<"bar">): string | string[] =>
                            {
                                return tooltipItem.label.toString();
                            },
                        },
                    },
                },
                elements: { line: { fill: false } },
                scales: {
                    x: {
                        display: true,
                        grid: { display: true },
                        labels: this.chartCriteria.ChartDatas[0].Labels.map(x => x.substring(0, 3)),
                    },
                    y: {
                        type: "linear",
                        display: true,
                        position: "left",
                        grid: { display: true },
                        ticks: {
                            callback: (value: number) =>
                            {
                                return formatAbbreviatedNumber(value > -10 && value < 10 ? value.toFixed(2) : value.toFixed(0));
                            },
                        },
                    },
                },
            },
            data: {
                datasets: [
                    {
                        type: "bar",
                        label: budgeted.SetLabel,
                        data: budgeted.Data,
                        backgroundColor: chartColors[0],
                        borderColor: chartColors[0],
                        hoverBackgroundColor: chartColors[0],
                        hoverBorderColor: chartColors[0],
                        categoryPercentage: 0.5,
                        barPercentage: 0.8,
                    },
                    {
                        type: "bar",
                        label: filled.SetLabel,
                        data: filled.Data,
                        backgroundColor: chartColors[1],
                        borderColor: chartColors[1],
                        hoverBackgroundColor: chartColors[1],
                        hoverBorderColor: chartColors[1],
                        categoryPercentage: 0.5,
                        barPercentage: 0.8,
                    },
                    {
                        type: "bar",
                        label: vacant.SetLabel,
                        data: vacant.Data,
                        borderColor: chartColors[2],
                        backgroundColor: chartColors[2],
                        hoverBackgroundColor: chartColors[2],
                        hoverBorderColor: chartColors[2],
                        categoryPercentage: 0.5,
                        barPercentage: 0.8,
                    },
                    {
                        type: "bar",
                        label: closed.SetLabel,
                        data: closed.Data,
                        borderColor: chartColors[3],
                        backgroundColor: chartColors[3],
                        hoverBackgroundColor: chartColors[3],
                        hoverBorderColor: chartColors[3],
                        categoryPercentage: 0.5,
                        barPercentage: 0.8,
                    },
                ],
            },
        };

        this.showChart.set(this.chartCriteria?.ChartDatas?.length > 0);
    }
}
